<script setup lang="ts">
import { computed } from 'vue'
import { Check, Circle } from 'lucide-vue-next'
import { PASSWORD_RULES, passwordStrength } from '../utils/passwordPolicy'

// `id` goes on the root so the password input can point at it with aria-describedby.
const props = defineProps<{ password: string; id: string }>()

const rules = computed(() => PASSWORD_RULES.map(rule => ({ ...rule, met: rule.test(props.password) })))
const strength = computed(() => passwordStrength(props.password))
const filledSegments = computed(() => ({ Weak: 1, Fair: 2, Strong: 3 })[strength.value])
const strengthColor = computed(() => ({ Weak: 'bg-cc-danger', Fair: 'bg-cc-warning', Strong: 'bg-cc-success' })[strength.value])
</script>

<template>
  <div :id="id" class="mt-2 space-y-2 text-xs">
    <!-- Text only changes when a rule flips, so polite announcements stay per-status, not per-keystroke -->
    <ul aria-live="polite" class="space-y-1">
      <li
        v-for="rule in rules"
        :key="rule.id"
        class="flex items-center gap-1.5"
        :class="rule.met ? 'text-cc-success' : 'text-cc-text-secondary'"
      >
        <Check v-if="rule.met" class="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
        <Circle v-else class="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
        <span>{{ rule.label }}</span>
        <span class="sr-only">{{ rule.met ? '(met)' : '(not met)' }}</span>
      </li>
    </ul>
    <div class="flex items-center gap-2">
      <div class="flex flex-1 gap-1" aria-hidden="true">
        <span
          v-for="n in 3"
          :key="n"
          class="h-1.5 flex-1 rounded-full"
          :class="password && n <= filledSegments ? strengthColor : 'bg-cc-track'"
        />
      </div>
      <p aria-live="polite" class="w-28 text-right text-cc-text-secondary">
        <template v-if="password">Strength: <span class="font-semibold text-cc-text">{{ strength }}</span></template>
      </p>
    </div>
  </div>
</template>
